'use client'

import { useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

import {
  applyShellUpdateNow,
  pendingShellUpdate,
  SHELL_UPDATE_EVENT,
  type PendingShellUpdate,
} from '@/lib/ui-variant/shell-sync'

export const SHELL_UPDATE_TOAST_ID = 'ui-shell-update'

/**
 * "Hay una versión nueva" (bug bash SW-1). When the UI switch flips while
 * the person has text unsent or an upload running, shell sync does not
 * reload (lib/ui-variant/shell-sync.ts): it leaves the change for the next
 * navigation and asks for this notice. It stays until they update, leave
 * the page (that navigation loads the new version) or the server goes back
 * to this tab's version. A toast in both looks (the root layout's Toaster),
 * at the top so it never covers a composer, announced politely by Sonner's
 * live region, with one Update button.
 */
export function UiShellUpdateNotice() {
  const t = useTranslations('common.shellUpdate')

  useEffect(() => {
    const show = (pending: PendingShellUpdate | null) => {
      if (!pending?.notify) {
        toast.dismiss(SHELL_UPDATE_TOAST_ID)
        return
      }
      toast(t('message'), {
        id: SHELL_UPDATE_TOAST_ID,
        duration: Infinity,
        // On top: at the bottom it covered the very field the person is writing in.
        position: 'top-center',
        action: { label: t('action'), onClick: () => applyShellUpdateNow(window) },
      })
    }
    show(pendingShellUpdate(window))
    const onUpdate = (event: Event) =>
      show((event as CustomEvent<PendingShellUpdate | null>).detail ?? null)
    window.addEventListener(SHELL_UPDATE_EVENT, onUpdate)
    return () => window.removeEventListener(SHELL_UPDATE_EVENT, onUpdate)
  }, [t])

  return null
}
